import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'default' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'icon';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leading?: ReactNode;
  trailing?: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-s3 font-body font-semibold rounded-sm border transition-all duration-fast ease-soft active:scale-[0.98] disabled:opacity-45 disabled:cursor-not-allowed select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss-300 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

const variants: Record<Variant, string> = {
  primary:
    'bg-moss-500 border-moss-400 text-fg-1 shadow-1 hover:bg-moss-400 hover:border-moss-300',
  default:
    'bg-card border-[var(--border)] text-fg-1 shadow-[inset_0_1px_0_rgba(236,235,227,0.04),0_1px_2px_rgba(0,0,0,0.4)] hover:bg-card-hover hover:border-[var(--border-strong)]',
  ghost:
    'bg-transparent border-[var(--border)] text-fg-1 hover:bg-card hover:border-[var(--border-strong)]',
  danger:
    'bg-transparent border-petal-700 text-petal-300 hover:border-petal-500 hover:bg-[rgba(185,111,108,0.08)]',
};

const sizes: Record<Size, string> = {
  sm: 'text-[12px] px-s4 py-[6px]',
  md: 'text-[13px] px-s5 py-[9px]',
  icon: 'p-[9px] text-[13px]',
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ variant = 'default', size = 'md', leading, trailing, className, children, ...rest }, ref) => (
    <button ref={ref} className={cn(base, variants[variant], sizes[size], className)} {...rest}>
      {leading}
      {children}
      {trailing}
    </button>
  ),
);
Button.displayName = 'Button';

export default Button;
